/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import type { TFunction } from 'i18next';
import type {
  GuestReview,
  GuestReviewsApiResponse,
  GuestReviewsByHotelResponse,
  GuestReviewsResponse,
  HotelDetailResponse,
  ReviewCategoryScore,
} from '../types';

export const MAX_PREVIEW_REVIEWS = 3;
export const REVIEW_SCORE_MAX = 5;
export const REVIEW_LOGO_BASE_URL =
  'https://storage.googleapis.com/static.airasia.com/design/logos';

type ReviewsTranslation = TFunction<'hotels'>;
type SearchReviewsTranslation = (
  key: string,
  options?: { count?: number },
) => string;

type LegacyGuestReviewsResponse = {
  reviews?: GuestReviewsResponse['reviews'] | GuestReviewsByHotelResponse;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const isGuestReviewsByHotelResponse = (
  value: unknown,
): value is GuestReviewsByHotelResponse =>
  isRecord(value) &&
  Object.values(value).some(
    (item) => isRecord(item) && Array.isArray(item.reviews),
  );

const parseScore = (value?: string | number | null) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }

  const parsed = Number.parseFloat(value || '');
  return Number.isFinite(parsed) ? parsed : undefined;
};

const formatDecimalScore = (value?: string | number | null) => {
  const parsed = parseScore(value);
  return parsed === undefined ? undefined : parsed.toFixed(1);
};

const toProgress = (value?: string | number | null) => {
  const parsed = parseScore(value);
  if (parsed === undefined) {
    return 0;
  }

  return Math.min(100, Math.max(0, (parsed / REVIEW_SCORE_MAX) * 100));
};

export const normalizeGuestReviewsResponse = (
  response?: GuestReviewsApiResponse | LegacyGuestReviewsResponse | null,
): GuestReview[] => {
  if (!response) {
    return [];
  }

  if ('reviews' in response) {
    const reviewsPayload = response.reviews;

    if (Array.isArray(reviewsPayload)) {
      return reviewsPayload;
    }

    return isGuestReviewsByHotelResponse(reviewsPayload)
      ? getFirstKeyedReviews(reviewsPayload)
      : [];
  }

  return isGuestReviewsByHotelResponse(response)
    ? getFirstKeyedReviews(response)
    : [];
};

const getFirstKeyedReviews = (
  response?: GuestReviewsByHotelResponse,
): GuestReview[] => {
  const firstHotelReviews = Object.values(response || {}).find((item) =>
    Array.isArray(item?.reviews),
  )?.reviews;
  return firstHotelReviews || [];
};

export const getReviewSourceLabel = (review: GuestReview) =>
  review.source?.trim() || review.supplier?.trim() || '';

export const buildReviewLogoUrl = (source?: string) => {
  const trimmedSource = source?.trim();
  if (!trimmedSource) {
    return undefined;
  }

  return `${REVIEW_LOGO_BASE_URL}/${encodeURIComponent(trimmedSource)}.png`;
};

export const formatReviewScore = (value?: string | number | null) => {
  const formatted = formatDecimalScore(value);
  return formatted ? `${formatted}/${REVIEW_SCORE_MAX}` : '';
};

export const formatReviewCardScore = (value?: string | number | null) =>
  formatDecimalScore(value) || '';

export const getSearchReviewCountLabel = (
  count: number | string,
  t: SearchReviewsTranslation,
) => {
  const reviewLabelKey =
    Number(count) === 1
      ? 'searchHotelPage.hotelDeals.review'
      : 'searchHotelPage.hotelDeals.reviews';

  return `${count} ${t(reviewLabelKey)}`;
};

export const getReviewAuthorDate = (review: GuestReview) =>
  [review.reviewer_name, review.date_submitted]
    .map((item) => item?.trim())
    .filter(Boolean)
    .join(', ');

export const getRatingLabelFallback = (
  rating: string | number | undefined,
  t: ReviewsTranslation,
) => {
  const score = parseScore(rating);

  if (score === undefined) {
    return '';
  }

  if (score >= 4.5) {
    return t('hotelDetailsPage.reviews.ratingLabel.excellent');
  }
  if (score >= 4) {
    return t('hotelDetailsPage.reviews.ratingLabel.veryGood');
  }
  if (score >= 3) {
    return t('hotelDetailsPage.reviews.ratingLabel.good');
  }
  if (score >= 2) {
    return t('hotelDetailsPage.reviews.ratingLabel.okay');
  }

  return t('hotelDetailsPage.reviews.ratingLabel.poor');
};

const getReviewCountLabel = (
  count: number | undefined,
  t: ReviewsTranslation,
) => t('hotelDetailsPage.reviews.reviewCount', { count: count || 0 });

export const getReviewCategories = (
  guestRating: HotelDetailResponse['guest_rating'] | undefined,
  t: ReviewsTranslation,
): ReviewCategoryScore[] => {
  const categoryConfig = [
    {
      id: 'cleanliness',
      label: t('hotelDetailsPage.reviews.categories.cleanliness'),
      value: guestRating?.cleanliness,
    },
    {
      id: 'service',
      label: t('hotelDetailsPage.reviews.categories.service'),
      value: guestRating?.service,
    },
    {
      id: 'comfort',
      label: t('hotelDetailsPage.reviews.categories.comfort'),
      value: guestRating?.comfort,
    },
    {
      id: 'conditions',
      label: t('hotelDetailsPage.reviews.categories.conditions'),
      value: guestRating?.condition,
    },
    {
      id: 'amenities',
      label: t('hotelDetailsPage.reviews.categories.amenities'),
      value: guestRating?.amenities,
    },
    {
      id: 'neighbourhood',
      label: t('hotelDetailsPage.reviews.categories.neighbourhood'),
      value: guestRating?.neighbourhood || guestRating?.location,
    },
  ];

  return categoryConfig
    .map((item) => ({
      id: item.id,
      label: item.label,
      score: formatDecimalScore(item.value) || '',
      progress: toProgress(item.value),
    }))
    .filter((item) => Boolean(item.score));
};

export const getReviewSummary = (
  hotelDetail: HotelDetailResponse | undefined,
  reviews: GuestReview[],
  t: ReviewsTranslation,
) => {
  const firstReview = reviews[0];
  const rating =
    hotelDetail?.guest_rating?.rating ?? hotelDetail?.guestRating?.rating;
  const ratingCount =
    hotelDetail?.guest_rating?.count ?? hotelDetail?.guestRating?.count;
  const normalizedRating = rating;
  const hasRating = parseScore(normalizedRating) !== undefined;

  if (!hasRating) {
    return {
      score: `0/${REVIEW_SCORE_MAX}`,
      cardScore: '0',
      label: t('hotelDetailsPage.reviews.noReview'),
      reviewCountLabel: '',
      categories: [],
    };
  }

  const label =
    hotelDetail?.guestRating?.labelId ||
    getRatingLabelFallback(normalizedRating, t) ||
    firstReview?.rating_label ||
    '';

  return {
    score: formatReviewScore(normalizedRating),
    cardScore: formatReviewCardScore(normalizedRating),
    label,
    reviewCountLabel: getReviewCountLabel(ratingCount || reviews.length, t),
    categories: getReviewCategories(hotelDetail?.guest_rating, t),
  };
};
